<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveCommunityDetailStore } from '../../lib/convexRealtime';
  import {
    fetchQuestionnaireForApplicant,
    joinCommunity,
    leaveCommunity
  } from '../../lib/convexApi';
  import type {
    AnswerValue,
    CommunityDetail,
    QuestionDefinition
  } from '../../lib/types';
  import { session, openSignIn } from '../session';
  import { gotoCommunityManage, gotoHome, gotoEvent } from '../router';
  import { accentStyle, cardAction, formatEventDate, formatPrice } from '../format';
  import type { CommunityEventSummary } from '../../lib/types';
  import QuestionnaireRenderer from '../components/QuestionnaireRenderer.svelte';
  import MarkdownView from '../ui/MarkdownView.svelte';
  import Notice from '../ui/Notice.svelte';
  import ErrorPanel from '../ui/ErrorPanel.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let slug: string;

  let store: ReturnType<typeof liveCommunityDetailStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedSlug: string | null = null;

  let community: CommunityDetail | null = null;
  let loading = true;
  let error: string | null = null;

  let actionPending = false;
  let actionError: string | null = null;
  let applicationNote = '';
  let showApplyForm = false;
  let applicantQuestions: QuestionDefinition[] | null = null;
  let applicantQuestionsVersion = 0;
  let questionsLoading = false;
  let questionnaireRenderer: QuestionnaireRenderer | null = null;

  $: if (slug && slug !== subscribedSlug) {
    subscribedSlug = slug;
    showApplyForm = false;
    actionError = null;
    unsubscribe?.();
    store?.dispose();
    store = liveCommunityDetailStore(slug);
    unsubscribe = store.subscribe(state => {
      community = state.data ?? null;
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  $: membershipStatus = community?.membership?.status ?? null;
  $: isActiveMember = membershipStatus === 'active';
  $: needsApproval = community?.visibility !== 'public';
  $: usesQuestionnaire =
    (community?.applicationMode ?? 'note') === 'questionnaire' && community?.hasQuestionnaire === true;

  async function openApplyForm() {
    if (!$session.signedIn) {
      openSignIn();
      return;
    }
    showApplyForm = true;
    if (usesQuestionnaire && !applicantQuestions) {
      questionsLoading = true;
      try {
        const questionnaire = await fetchQuestionnaireForApplicant(slug);
        applicantQuestions = questionnaire?.questions ?? null;
        applicantQuestionsVersion = questionnaire?.version ?? 0;
      } catch {
        applicantQuestions = null;
      } finally {
        questionsLoading = false;
      }
    }
  }

  async function handleQuestionnaireSubmit(event: CustomEvent<Record<string, AnswerValue>>) {
    actionPending = true;
    actionError = null;
    try {
      await joinCommunity(slug, null, event.detail);
      questionnaireRenderer?.clearDraft();
      showApplyForm = false;
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Could not apply. Please try again.';
    } finally {
      actionPending = false;
    }
  }

  const visibilityLabel: Record<string, string> = {
    public: 'Open community',
    apply: 'Apply to join',
    hidden: 'Private community'
  };

  async function handleJoin(note?: string) {
    if (!$session.signedIn) {
      openSignIn();
      return;
    }
    actionPending = true;
    actionError = null;
    try {
      await joinCommunity(slug, note ?? null);
      showApplyForm = false;
      applicationNote = '';
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Could not join. Please try again.';
    } finally {
      actionPending = false;
    }
  }

  async function handleLeave() {
    actionPending = true;
    actionError = null;
    try {
      await leaveCommunity(slug);
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Could not leave. Please try again.';
    } finally {
      actionPending = false;
    }
  }

  // Row CTA from a community event. Same state→action map as the catalog card
  // (shared cardAction helper); a stranger's community apply-path is this very
  // community (visibility !== 'hidden'). The row stays pure navigation into
  // /e/<id>; the button below only calls gotoEvent. `owned` isn't tracked on
  // this page — a held ticket surfaces as the server's 'going' standing.
  function rowAction(event: CommunityEventSummary) {
    return cardAction({
      standing: event.standing,
      soldOut: event.soldOut,
      ticketPrice: event.ticketPrice,
      applicationUrl: event.applicationUrl,
      hasCommunityApplyPath: community != null && community.visibility !== 'hidden',
      owned: false
    });
  }
</script>

<div class="community container themed" style={accentStyle(community?.themeColor)}>
  <button type="button" class="community__back" on:click={() => gotoHome({ push: false })}>
    <span aria-hidden="true">←</span> All events
  </button>

  {#if loading && !community}
    <LoadingBlock label="Loading community…" />
  {:else if error}
    <ErrorPanel
      title="This community link didn't work"
      hint="Double-check the link you were sent, or try again in a moment."
      detail={`${error}\ncommunity slug=${slug}`}
    />
  {:else if !community}
    <ErrorPanel
      title="We couldn't find this community"
      hint="Double-check the link you were sent, or contact the organizers."
      detail={`No community found for slug=${slug}`}
    />
  {:else}
    <header class="community__header surface surface--raised">
      <div class="community__title">
        {#if community.logoUrl}
          <img class="community__logo" src={community.logoUrl} alt="{community.name} logo" loading="lazy" />
        {/if}
        <p class="community__eyebrow">{visibilityLabel[community.visibility] ?? 'Community'}</p>
        <h1>{community.name}</h1>
        {#if community.description}
          <div class="community__description">
            <MarkdownView source={community.description} />
          </div>
        {/if}
        {#if community.memberCount != null}
          <p class="community__members">{community.memberCount} {community.memberCount === 1 ? 'member' : 'members'}</p>
        {/if}
      </div>

      <div class="community__membership">
        {#if isActiveMember}
          <span class="community__badge community__badge--active">
            {community.membership?.role === 'owner'
              ? 'Owner'
              : community.membership?.role === 'admin'
              ? 'Admin'
              : 'Member'}
          </span>
          {#if community.membership?.role === 'member'}
            <button type="button" class="ghost" on:click={handleLeave} disabled={actionPending}>Leave</button>
          {/if}
        {:else if membershipStatus === 'pending'}
          <span class="community__badge">Application pending</span>
        {:else if showApplyForm}
          {#if usesQuestionnaire}
            <div class="community__apply-form community__apply-form--wide">
              {#if questionsLoading}
                <LoadingBlock label="Loading application…" />
              {:else if applicantQuestions && applicantQuestions.length > 0}
                <QuestionnaireRenderer
                  bind:this={questionnaireRenderer}
                  questions={applicantQuestions}
                  submitLabel="Send application"
                  draftKey={`gravity:questionnaire:community:${slug}:${applicantQuestionsVersion}`}
                  busy={actionPending}
                  on:submit={handleQuestionnaireSubmit}
                  on:cancel={() => (showApplyForm = false)}
                />
              {:else}
                <form on:submit|preventDefault={() => handleJoin(applicationNote)}>
                  <textarea
                    bind:value={applicationNote}
                    rows="3"
                    maxlength="1000"
                    placeholder="A line about who you are (optional)"
                  ></textarea>
                  <div class="community__apply-actions">
                    <button type="button" class="ghost" on:click={() => (showApplyForm = false)}>Cancel</button>
                    <button type="submit" class="accent" disabled={actionPending}>
                      {actionPending ? 'Sending…' : 'Send application'}
                    </button>
                  </div>
                </form>
              {/if}
            </div>
          {:else}
            <form class="community__apply-form" on:submit|preventDefault={() => handleJoin(applicationNote)}>
              <textarea
                bind:value={applicationNote}
                rows="3"
                maxlength="1000"
                placeholder="A line about who you are (optional)"
              ></textarea>
              <div class="community__apply-actions">
                <button type="button" class="ghost" on:click={() => (showApplyForm = false)}>Cancel</button>
                <button type="submit" class="accent" disabled={actionPending}>
                  {actionPending ? 'Sending…' : 'Send application'}
                </button>
              </div>
            </form>
          {/if}
        {:else if needsApproval}
          <button type="button" class="accent" on:click={openApplyForm}>
            Apply to join
          </button>
        {:else}
          <button type="button" class="accent" on:click={() => handleJoin()} disabled={actionPending}>
            {actionPending ? 'Joining…' : 'Join'}
          </button>
        {/if}
      </div>
    </header>

    {#if actionError}
      <Notice variant="error">{actionError}</Notice>
    {/if}

    {#if community.restricted}
      <Notice>This community is private. Membership is required to see its events.</Notice>
    {:else}
      <section class="community__events" aria-labelledby="community-events-heading">
        <h2 id="community-events-heading">Events</h2>
        {#if community.events.length === 0}
          <p class="community__empty">No events yet.</p>
        {:else}
          <ul>
            {#each community.events as event (event.id)}
              {@const action = rowAction(event)}
              <li>
                <!-- Stretched-link row: the name link owns the whole row surface
                     (::after inset:0); the compact CTA layers above it. Both are
                     pure navigation into /e/<id> — the event page owns the real
                     claim/apply/buy flows per standing. -->
                <div class="community-event surface surface--sunken">
                  {#if event.flyerUrl}
                    <img class="community-event__flyer" src={event.flyerUrl} alt="" loading="lazy" />
                  {/if}
                  <span class="community-event__info">
                    <a
                      class="community-event__name community-event__link"
                      href={`/e/${event.id}`}
                      on:click|preventDefault={() => gotoEvent(event.id, { push: true })}
                    >{event.name}</a>
                    {#if formatEventDate(event.schedule)}
                      <span class="community-event__meta">{formatEventDate(event.schedule)}</span>
                    {/if}
                    {#if event.location?.name}
                      <span class="community-event__meta">{event.location.name}</span>
                    {/if}
                  </span>
                  <span class="community-event__side">
                    {#if event.ticketPrice != null && event.ticketPrice > 0}
                      <span class="community-event__price">{formatPrice(event.ticketPrice, event.currency)}</span>
                    {/if}
                    {#if action.kind === 'button'}
                      <button
                        type="button"
                        class="community-event__cta"
                        class:community-event__cta--accent={action.tone === 'accent'}
                        class:community-event__cta--success={action.tone === 'success'}
                        class:community-event__cta--ghost={action.tone === 'ghost'}
                        class:community-event__cta--disabled={action.tone === 'disabled'}
                        disabled={action.disabled}
                        on:click|stopPropagation={() => gotoEvent(event.id, { push: true })}
                      >
                        {action.label}
                      </button>
                    {:else}
                      <span class="community-event__wait">{action.label}</span>
                    {/if}
                  </span>
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {/if}

    {#if community.isManager}
      <div class="community__manage-bar">
        <h2>Manage</h2>
        <button type="button" class="accent" on:click={() => gotoCommunityManage(community?.slug ?? slug)}>
          Open community admin
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  .community {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-4) var(--space-8);
    max-width: var(--content-max-width);
  }

  .community__back {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    cursor: pointer;
  }

  .community__back:hover {
    color: var(--color-text-primary);
  }

  .community__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-5);
    padding: var(--space-6);
    border-radius: var(--radius-xl);
    flex-wrap: wrap;
  }

  .community__eyebrow {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-text-amber);
  }

  .community__logo {
    display: block;
    max-height: 64px;
    max-width: 220px;
    object-fit: contain;
    margin-bottom: var(--space-3);
  }

  .community__title h1 {
    margin: 0 0 var(--space-2);
    font-size: clamp(1.6rem, 4vw, 2.2rem);
  }

  .community__description {
    margin: 0 0 var(--space-2);
    color: var(--color-text-secondary);
    line-height: var(--line-height-relaxed);
    max-width: 52ch;
  }

  .community__members {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .community__membership {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-end;
    min-width: 200px;
  }

  .community__badge {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: var(--border-ambient);
    color: var(--color-text-amber);
    font-size: var(--font-size-sm);
    font-weight: 600;
  }

  .community__badge--active {
    border-color: rgba(123, 228, 149, 0.4);
    color: var(--color-success-500);
  }

  .community__apply-form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    width: min(320px, 100%);
  }

  .community__apply-form--wide {
    width: min(440px, 100%);
  }

  .community__apply-form form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .community__apply-form textarea {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    resize: vertical;
  }

  .community__apply-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
  }

  .community__events h2 {
    margin: 0 0 var(--space-4);
    font-size: var(--font-size-xl);
  }

  .community__events ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Non-interactive row container for the stretched-link pattern: the name link
     stretches over the whole row, the compact CTA layers above it. */
  .community-event {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    flex-wrap: wrap;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-lg);
    color: var(--color-text-primary);
    border: var(--border-subtle);
    transition: border-color var(--transition-fast), transform var(--transition-fast);
  }

  .community-event:hover {
    border-color: rgb(var(--accent-rgb) / 0.4);
    transform: translateY(-1px);
  }

  .community__manage-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  .community__manage-bar h2 {
    margin: 0;
    font-size: var(--font-size-xl);
  }

  .community-event__flyer {
    flex: 0 0 auto;
    width: 96px;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    display: block;
    border: var(--border-subtle);
  }

  .community-event__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    flex: 1 1 auto;
    min-width: 0;
  }

  .community-event__name {
    font-weight: 600;
  }

  /* Name is a real <a href="/e/<id>"> intercepted into gotoEvent; its ::after
     stretches over the whole row so the surface navigates. The CTA layers above. */
  .community-event__link {
    color: inherit;
    text-decoration: none;
  }

  .community-event__link::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
  }

  .community-event__link:focus-visible {
    outline: none;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .community-event__meta {
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .community-event__side {
    /* Above the name link's stretched pseudo-element so the CTA takes clicks. */
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .community-event__price {
    color: var(--color-text-amber);
    font-variant-numeric: tabular-nums;
  }

  /* Compact (non-block) row CTA. Same tone system as the catalog card button;
     explicit backgrounds so the global bare-button accent rule never leaks. */
  .community-event__cta {
    padding: 0.45rem 0.9rem;
    border: 1px solid transparent;
    border-radius: 0;
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    white-space: nowrap;
    transition: background var(--transition-fast), border-color var(--transition-fast),
      color var(--transition-fast);
  }

  .community-event__cta--accent {
    background: rgb(var(--accent-rgb));
    color: var(--color-surface-950);
  }

  .community-event__cta--accent:hover {
    background: color-mix(in srgb, rgb(var(--accent-rgb)), white 14%);
  }

  .community-event__cta--success {
    background: color-mix(in srgb, var(--color-success-500), transparent 82%);
    border-color: color-mix(in srgb, var(--color-success-500), transparent 45%);
    color: var(--color-success-500);
    cursor: default;
  }

  .community-event__cta--ghost {
    background: transparent;
    border-color: rgb(var(--accent-rgb) / 0.3);
    color: var(--color-text-secondary);
  }

  .community-event__cta--ghost:hover {
    border-color: rgb(var(--accent-rgb) / 0.5);
    color: var(--color-text-primary);
  }

  .community-event__cta--disabled,
  .community-event__cta--disabled:hover {
    background: var(--color-surface-800);
    color: var(--color-text-muted);
    cursor: default;
  }

  .community-event__wait {
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }

  .community__empty {
    margin: 0;
    color: var(--color-text-muted);
  }
</style>
